import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import type { BaseQuery } from './baseQuery.types';

type GetAccountUsersByOrganizationResponse =
  components['schemas']['GetAccountUsersByOrganizationEndpoint_GetAccountUsersByOrganizationPagedResponse'];

export interface UseAccountUsersByOrganizationReturn extends BaseQuery {
  accountUsers: GetAccountUsersByOrganizationResponse | null;
  totalCount: number;
}

export function useAccountUsersByOrganization(
  page: number = 1,
  pageSize: number = 50,
  organizationId: number,
  enabled: boolean = true,
): UseAccountUsersByOrganizationReturn {
  const { getAccessToken } = useAuth();
  const getAccountUsersByOrganization = async (page: number, pageSize: number, organizationId: number) => {
    const client = createApiClient(getAccessToken);
    const { data } = await client.GET(`/api/users/account-manager/by-organization/{organizationId}`, {
      params: {
        path: { organizationId },
        query: {
          Page: page,
          PageSize: pageSize,
        },
      },
    });

    if (!data) {
      throw new Error('No data returned from API');
    }

    return data as GetAccountUsersByOrganizationResponse;
  };

  const useAccountUsersByOrganizationQuery = useQuery({
    queryKey: ['accountUsers', page, pageSize, organizationId],
    queryFn: () => getAccountUsersByOrganization(page, pageSize, organizationId),
    enabled: enabled && organizationId > 0,
    retry: false,
  });

  const { data, isLoading, error, refetch, isRefetching, isPending } = useAccountUsersByOrganizationQuery;

  return {
    accountUsers: data ?? null,
    totalCount: data?.totalCount ?? 0,
    isLoading: isLoading || isPending,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
